// Menus (WAI-ARIA menu and menu button patterns) and our own right-click menu (D-062). Arrow keys move,
// Home/End jump, typing a letter jumps to the next item starting with it, Right opens a submenu and
// Left closes it, Enter or Space chooses, Escape closes one level, Tab closes all.
import { cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent, type ReactElement, type ReactNode } from 'react';
import type { Anchor, Placement, Point } from './layer';
import { Tooltip } from './tooltip';
import { clickGuard } from './click-guard';

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  /** Shown on the right, e.g. "E" or "⌘C". Only for shortcuts that really work. */
  shortcut?: string;
  /** A second, quieter line. */
  hint?: string;
  disabled?: boolean;
  /** Destructive (delete): coloured, never the first item. */
  danger?: boolean;
  /** A tick: makes it a checkable item (menuitemcheckbox). */
  checked?: boolean;
  onSelect?(): void;
  /** A submenu. */
  items?: MenuEntry[];
  testId?: string;
}
export type MenuEntry = MenuItem | { separator: true } | { heading: string } | false | null | undefined;

/** Drops empty entries and separators at the edges or next to each other. */
export function tidyMenu(entries: MenuEntry[]): Exclude<MenuEntry, false | null | undefined>[] {
  const out: Exclude<MenuEntry, false | null | undefined>[] = [];
  for (const e of entries) {
    if (!e) continue;
    if ('separator' in e && (!out.length || 'separator' in out[out.length - 1])) continue;
    out.push(e);
  }
  while (out.length && 'separator' in out[out.length - 1]) out.pop();
  return out;
}

export interface MenuProps {
  items: MenuEntry[];
  open: boolean;
  onClose(): void;
  anchor: Anchor;
  /** What the menu is about, for screen readers ("Email actions"). */
  label: string;
  placement?: Placement;
  /** Where focus starts: the first item (keyboard), the last, or the menu itself (pointer: nothing highlighted). */
  initialFocus?: 'menu' | 'first' | 'last';
  /** Element to focus after closing (defaults to the anchor element). */
  returnTo?: HTMLElement | null;
  within?: Element | null;
  id?: string;
  testId?: string;
}

/** A menu in a floating layer. Choosing an item closes it (and its parents), then runs `onSelect`.
 *  The layer's code (menu-layer.tsx) loads when the page is idle, or on the first open (a few ms). */
export function Menu({ open, ...rest }: MenuProps) {
  const [, loaded] = useState(0);
  useEffect(() => { if (open && !layerModule) loadLayer().then(() => loaded((n) => n + 1)).catch(() => {}); }, [open]);
  if (!open || !layerModule) return null;
  const { MenuLayer } = layerModule;
  return <MenuLayer {...rest} done={rest.onClose} />;
}

let layerModule: typeof import('./menu-layer') | null = null;
const loadLayer = () => import('./menu-layer').then((m) => { layerModule = m; return m; });
/** Loads the menu layer when the page is idle, so the first right-click or menu opens at once. */
function usePrefetchLayer() {
  useEffect(() => {
    if (layerModule) return;
    const w = window as Window & { requestIdleCallback?(cb: () => void, o?: { timeout: number }): number };
    const go = () => { loadLayer().catch(() => {}); };
    if (w.requestIdleCallback) w.requestIdleCallback(go, { timeout: 2000 }); else setTimeout(go, 300);
  }, []);
}

export interface MenuButtonProps {
  items: MenuEntry[] | (() => MenuEntry[]);
  /** The button's name; for an icon-only button also its tooltip. */
  label: string;
  icon?: ReactNode;
  /** Visible text; leave out for an icon button. */
  children?: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'soft';
  size?: 'sm' | 'md';
  className?: string;
  placement?: Placement;
  disabled?: boolean;
  testId?: string;
  /** Name of the menu, when it differs from the button ("More actions" → "Message actions"). */
  menuLabel?: string;
}

/** A button that opens a menu: click, Enter, Space or ArrowDown open it on the first item; ArrowUp on the last. */
export function MenuButton({ items, label, icon, children, variant = 'secondary', size = 'md', className, placement = 'bottom-start', disabled, testId, menuLabel }: MenuButtonProps) {
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const [open, setOpen] = useState<null | 'first' | 'last' | 'menu'>(null);
  usePrefetchLayer();
  // No visible text (no children, or only icons): the label names it and shows as a tooltip.
  const hasText = (n: ReactNode): boolean => typeof n === 'string' || typeof n === 'number' || (Array.isArray(n) && n.some(hasText));
  const iconOnly = !hasText(children);
  const cls = className ?? (children === undefined ? 'dz-icon-btn' : ['dz-btn', variant !== 'secondary' && `dz-btn--${variant}`, size === 'sm' && 'dz-btn--sm'].filter(Boolean).join(' '));
  const button = (
    <button ref={btn} type="button" className={cls} aria-haspopup="menu" aria-expanded={!!open} aria-controls={open ? id : undefined} aria-label={iconOnly ? label : undefined} disabled={disabled} data-testid={testId}
      onClick={(e) => setOpen(open ? null : e.detail === 0 ? 'first' : 'menu')}
      onKeyDown={(e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(e.key === 'ArrowDown' ? 'first' : 'last'); } }}>
      {icon}{children}
    </button>
  );
  return (
    <>
      {iconOnly ? <Tooltip content={label}>{button}</Tooltip> : button}
      {open && <Menu open id={id} items={typeof items === 'function' ? items() : items} onClose={() => setOpen(null)} anchor={btn.current} label={menuLabel ?? label} placement={placement} initialFocus={open} />}
    </>
  );
}

// ---------- Right-click and long-press ----------

type Surface = { onContextMenu(e: RMouseEvent): void; onPointerDown(e: RPointerEvent): void; onPointerMove(e: RPointerEvent): void; onPointerUp(): void; onPointerCancel(): void; onKeyDown(e: KeyboardEvent): void; onClickCapture(e: RMouseEvent): void; 'data-dz-context': '' };
interface Opened { items: MenuEntry[]; at: Point; label: string; returnTo: HTMLElement | null; focus: 'menu' | 'first' }

const LONG_PRESS = 500;
/** Right-click on text being edited or selected keeps the browser's menu (copy, paste, spelling). */
function wantsNativeMenu(e: RMouseEvent): boolean {
  if (e.shiftKey) return true; // Shift + right-click: the browser's own menu, for those who need it.
  const t = e.target as HTMLElement;
  if (t.closest('input, textarea, [contenteditable=""], [contenteditable="true"]')) return true;
  const sel = window.getSelection();
  if (sel && !sel.isCollapsed && sel.toString().trim() && sel.containsNode(t, true)) return true;
  return false;
}

/**
 * Our own right-click menu for app surfaces (rows, pills, events), as Gmail does on a message row.
 * `const ctx = useContextMenu();` then `<li {...ctx.bind(() => items, { label: 'Email actions' })}>` and
 * render `{ctx.menu}` once. Opens on right-click, touch long-press, the Menu key and Shift+F10.
 * `items` returning null or [] leaves the browser's menu alone.
 */
export function useContextMenu() {
  const [opened, setOpened] = useState<Opened | null>(null);
  usePrefetchLayer();
  const press = useRef<{ t: number; x: number; y: number; fired: boolean } | null>(null);
  const swallowClick = useRef(clickGuard());
  const close = useCallback(() => setOpened(null), []);
  const open = useCallback((at: Point | HTMLElement, items: MenuEntry[], label = 'Actions', focus: 'menu' | 'first' = 'first') => {
    const el = at instanceof HTMLElement ? at : null;
    const p = el ? (() => { const r = el.getBoundingClientRect(); return { x: r.left + Math.min(24, r.width / 2), y: r.top + Math.min(r.height, 40) }; })() : at as Point;
    setOpened({ items, at: p, label, returnTo: el ?? (document.activeElement as HTMLElement | null), focus });
  }, []);
  const bind = useCallback((items: () => MenuEntry[] | null | undefined, opts: { label?: string } = {}): Surface => ({
    'data-dz-context': '',
    onContextMenu: (e) => {
      if (press.current?.fired) { e.preventDefault(); return; } // Android also fires this after our long-press
      if (wantsNativeMenu(e)) return;
      const list = items();
      if (!list || !tidyMenu(list).length) return;
      e.preventDefault();
      setOpened({ items: list, at: { x: e.clientX, y: e.clientY }, label: opts.label ?? 'Actions', returnTo: e.currentTarget as HTMLElement, focus: e.detail === 0 && e.clientX === 0 ? 'first' : 'menu' });
    },
    onPointerDown: (e) => {
      swallowClick.current.press(); // a new press: an old long press's guard must not eat this tap (click-guard.ts)
      if (e.pointerType === 'mouse') return;
      const target = e.currentTarget as HTMLElement;
      const at = { x: e.clientX, y: e.clientY };
      const t = window.setTimeout(() => {
        const list = items();
        if (!list || !tidyMenu(list).length || !press.current) return;
        press.current.fired = true; swallowClick.current.longPressFired();
        setOpened({ items: list, at, label: opts.label ?? 'Actions', returnTo: target, focus: 'menu' });
      }, LONG_PRESS);
      press.current = { t, ...at, fired: false };
    },
    onPointerMove: (e) => { const p = press.current; if (p && !p.fired && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) { clearTimeout(p.t); press.current = null; } },
    onPointerUp: () => { const p = press.current; if (p) { clearTimeout(p.t); if (!p.fired) press.current = null; else setTimeout(() => { press.current = null; }, 400); } },
    onPointerCancel: () => { if (press.current) { clearTimeout(press.current.t); if (!press.current.fired) press.current = null; } },
    onClickCapture: (e) => { if (swallowClick.current.swallow()) { e.preventDefault(); e.stopPropagation(); } },
    onKeyDown: (e) => {
      if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
        const list = items();
        if (!list || !tidyMenu(list).length) return;
        e.preventDefault(); e.stopPropagation();
        open(e.currentTarget as HTMLElement, list, opts.label ?? 'Actions', 'first');
      }
    },
  }), [open]);
  const menu = useMemo(() => opened ? <Menu open items={opened.items} onClose={close} anchor={opened.at} label={opened.label} initialFocus={opened.focus} returnTo={opened.returnTo} within={opened.returnTo} /> : null, [opened, close]);
  return useMemo(() => ({ bind, menu, open, close, isOpen: !!opened }), [bind, menu, open, close, opened]);
}

/** The same as a component: wraps one element and gives it our right-click menu. */
export function ContextMenu({ items, label, children }: { items: () => MenuEntry[] | null | undefined; label?: string; children: ReactElement }) {
  const ctx = useContextMenu();
  if (!isValidElement(children)) return children;
  const own = children.props as Record<string, ((e: unknown) => void) | undefined>;
  const handlers = ctx.bind(items, { label }) as unknown as Record<string, unknown>;
  const merged: Record<string, unknown> = { 'data-dz-context': '' };
  for (const [k, h] of Object.entries(handlers)) {
    if (typeof h !== 'function') continue;
    merged[k] = (e: { defaultPrevented?: boolean }) => { own[k]?.(e); if (!e?.defaultPrevented || k === 'onPointerUp' || k === 'onPointerCancel') (h as (e: unknown) => void)(e); };
  }
  return <>{cloneElement(children, merged)}{ctx.menu}</>;
}
